import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'src/utils/i18n'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { messageInfo } from 'src/components/Message'
import { changeDownloadTips } from 'src/store/apps/Common'

// 备份提示步骤集合
const backupArr = [
  {
    url: '/images/ios-backup/step1.webp',
    alt: 'backup-step1',
    width: '229px',
    height: '379px',
    mbWidth: '229px',
    mbHeight: '379px',
    text: 'Open the Settings of the target device and click on the account name'
  },
  {
    url: '/images/ios-backup/step2.webp',
    alt: 'backup-step2',
    width: '229px',
    height: '379px',
    mbWidth: '229px',
    mbHeight: '379px',
    text: 'Tap iCloud'
  },
  {
    url: '/images/ios-backup/step3.webp',
    alt: 'backup-step3',
    width: '229px',
    height: '379px',
    mbWidth: '229px',
    mbHeight: '379px',
    text: 'Select iCloud Backup'
  },
  {
    url: '/images/ios-backup/step4.webp',
    alt: 'backup-step4',
    width: '229px',
    height: '379px',
    mbWidth: '229px',
    mbHeight: '379px',
    text: `Tap the slider next to iCloud Backup If its green youll know youve turned on iCloud backup and you can skip to the next step`
  },
  {
    url: '/images/ios-backup/step5.webp',
    alt: 'backup-step5',
    width: '229px',
    height: '379px',
    mbWidth: '229px',
    mbHeight: '379px',
    text: 'Make sure to enable syncing for all the apps you want to monitor'
  },
  {
    url: '/images/ios-backup/step6.webp',
    alt: 'backup-step6',
    width: '371px',
    height: '379px',
    mbWidth: '325px',
    mbHeight: '332px',
    text: 'Finally click Back Up Now Wait until the backup process complete While your iPhone is backing up your data You must remain connected to Wi-Fi'
  },
  {
    url: '/images/ios-backup/step7.webp',
    alt: 'backup-step7',
    width: '421px',
    height: '379px',
    mbWidth: '325px',
    mbHeight: '293px',
    text: 'Make sure there is enough storage left in the target iCloud account When the iCloud is full you can only see the previous backup data if you wanna view more data you can consider upgrading the iCloud storage plan'
  },
  {
    url: '/images/ios-backup/step8.png',
    alt: 'backup-step8',
    width: '180px',
    height: '379px',
    mbWidth: '180px',
    mbHeight: '379px',
    text: 'Turn on the Access iCloud Data on Web toggle'
  }
]

const IosBackup = ({ isModalOpen, setIsModalOpen, type = 0 }) => {

  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const store = useSelector(state => state.Common)
  const [current, setCurrent] = useState(0) // 当前的步骤

  const swiperRef = useRef(null)
  const prevRef = useRef(null) // 上一步按钮
  const nextRef = useRef(null) // 下一步按钮
  const paginationRef = useRef(null) // 圆点导航按钮
  const [innerWidth, setInnerWidth] = useState(0) // 实时屏幕宽度
  const downloadTips = [true, 'true'].includes(store.downloadTips) // 显示下载提示弹窗

  // 监听屏幕宽度
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handlerInnerWidth = () => {
        setInnerWidth(window.innerWidth)
      }

      handlerInnerWidth()

      window.addEventListener('resize', handlerInnerWidth)

      return () => {
        window.removeEventListener('resize', handlerInnerWidth)
      }
    }
  }, [])

  // 模态框关闭事件
  const modalOnCancel = () => {
    setIsModalOpen(false)

    // 是完成验证时关闭弹窗弹窗下载提示信息
    if (type == 0 && downloadTips) {
      messageInfo({
        content: t('Please wait patiently for the data to download If the data is still not updated after half an hour it is likely that the target device has not enabled backup'),
        duration: 10
      })

      // 清除允许弹窗状态
      dispatch(changeDownloadTips(false))
    }
  }

  // 每次打开弹窗重置到步骤一
  useEffect(() => {
    if (isModalOpen && swiperRef.current) {
      swiperRef.current.slideTo(0)
      setCurrent(0)
    }
  }, [isModalOpen])

  return (
    <Modal
      open={isModalOpen}
      onCancel={modalOnCancel}
      maskClosable={innerWidth > 768} // 是否允许点击弹窗外蒙版时触发关闭
      width={600}
      footer={null}
      centered={true}
      styles={{
        content: {
          padding: innerWidth > 768 ? '50px 50px 19px' : '40px 10px 8px',
          borderRadius: '16px',
          position: 'relative'
        }
      }}
      zIndex={1314}
      closeIcon={
        <CloseOutlined style={{ fontSize: innerWidth > 768 ? '20px' : '18px' }} />  // 修改這裡的大小
      }
    >
      <div className='flex flex-col items-center min-h-[671px] Mlg:h-auto'>

        {/* apple Icon */}
        <Image src='/images/apps/apple.svg' alt='img' className='w-[60px] h-[69px] Mlg:w-[35px] Mlg:h-[40px]' width='60' height='69' />

        {/* 蓝色标题 */}
        <p className='mt-[24px] text-[30px] Mlg:text-[20px] text-[#2405F2] text-center font-[600] leading-[40px] Mlg:leading-[30px]'>{t('Turn on the iCloud Backup')}</p>

        {/* 温馨提示 */}
        <div className='w-max max-w-full my-[16px] flex items-start justify-center text-[14px] Mlg:text-[10px] text-[#F11E34] leading-[21px] Mlg:leading-[15px] p-[8px_20px_8px_16px] Mlg:p-[14px_17px_11px] bg-[#FF00000D] rounded-[6px]'>
          <Image
            src='/images/apps/waring.svg'
            className='flex items-start mt-[5px] Mlg:mt-[3px] mr-[8px]'
            alt='img'
            width={12}
            height={12}
          />
          <p className='text-center'>
            {t('You can only get all data if iCloud is turned on')}
          </p>
        </div>

        {/* 步骤图&文案 */}
        <Swiper
          key={innerWidth <= 768 ? 'mobile' : 'desktop'} // 触发组件重新挂载
          allowTouchMove={innerWidth <= 768}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
          modules={[Navigation, Pagination]}
          direction='horizontal'
          navigation={{	//自定义切换箭头
            prevEl: prevRef.current,
            nextEl: nextRef.current
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current
            swiper.params.navigation.nextEl = nextRef.current
            swiper.params.pagination.el = paginationRef.current
            swiper.params.pagination.renderBullet = (_, className) =>
              `<div class="${className} myBullet"></div>`
          }}
          pagination={{
            clickable: true,
            el: paginationRef.current,
            renderBullet: (_, className) => {
              return `<div class="${className} myBullet"></div>`
            }
          }}
          onSlideChange={(e) => setCurrent(e.realIndex)}
          className='w-full relative'
        >
          {
            backupArr.map((item, index) => (
              <SwiperSlide key={index} className='h-full flex flex-col justify-center items-center'>
                <div className='h-[379px] mb-[24px] Mlg:mb-[48px]'>
                  <Image src={item.url} alt={item.alt} width={100} height={100}
                    style={{ width: innerWidth > 768 ? item.width : item.mbWidth, height: innerWidth > 768 ? item.height : item.mbHeight }}
                    className='object-cover object-left-top [box-shadow:0px_4px_18px_0px_#00000033] select-none' />
                </div>
                <p className='w-full text-[14px] text-[#000000] text-center leading-[22px]'>{t(item.text)}</p>
              </SwiperSlide>
            ))
          }

          {/* 切换步骤箭头按钮 */}
          <div className='w-full Mlg:hidden absolute top-[40%] -translate-y-[50%] z-20 select-none'>
            <div ref={prevRef} className='banner_prev absolute left-0'>
              <Image src='/images/ios-backup/arrow-left.svg' alt='arrow-left' width={24} height={24} className={`w-[24px] h-[24px] ${current === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100 cursor-pointer'}`} />
            </div>
            <div ref={nextRef} className='banner_next absolute right-0'>
              <Image src='/images/ios-backup/arrow-right.svg' alt='arrow-right' width={24} height={24} className={`w-[24px] h-[24px] ${current === backupArr.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100 cursor-pointer'}`} />
            </div>
          </div>

          {/* 圆点导航 */}
          <div ref={paginationRef} className='!h-[8px] flex justify-center items-center gap-x-[5px] absolute !top-[403px] !left-[50%] !-translate-x-[50%] z-20 lg:hidden'></div>
        </Swiper>
      </div>
    </Modal >
  )
}

export default IosBackup
